import { useFloating, shift, offset, arrow, flip, autoUpdate } from "@floating-ui/react";
import { FloatingPortal } from "@floating-ui/react-dom-interactions";
import { observer } from "mobx-react-lite";
import {
  type PropsWithChildren,
  useEffect,
  useId,
  useRef,
  type MutableRefObject,
  type ReactNode,
} from "react";
import { Button, ButtonIntent, ButtonState } from "src/components/Button";
import { Cluster } from "src/components/Cluster";
import { toolboxMenu } from "src/types/ToolboxMenuState";

/** @todo - this need to be refactored to support alt and non-alt click at same time */
type Props = PropsWithChildren<{
  Icon: (props: { className: string }) => ReactNode;
  onAltKey?: boolean;
  title?: string;
}>;

export const ListToolButton = observer(function ListToolButton({ children, Icon, title }: Props) {
  const id = useId();
  const isOpen = toolboxMenu.isOpen(id);
  const arrowRef = useRef<SVGSVGElement>(null);

  const {
    x,
    y,
    refs,
    strategy,
    middlewareData: { arrow: { y: arrowY } = {} },
    refs: { floating: floatingRef, reference: referenceRef },
  } = useFloating({
    middleware: [
      shift({ padding: 8 }),
      offset({ mainAxis: 12 }),
      arrow({ element: arrowRef as MutableRefObject<HTMLElement | null> }),
      flip(),
    ],
    placement: "right",
    whileElementsMounted: autoUpdate,
  });
  // While this menu is open, close it on a mousedown anywhere outside the menu
  // or its trigger. Clicks on a sibling toolbox button close this menu via the
  // shared `toolboxMenu` store instead — the toolbox stops mousedown propagation
  // before it reaches this window listener.
  useEffect(() => {
    if (!isOpen) return;
    const onMousedown = ({ target }: MouseEvent) => {
      let element = target as HTMLElement | null | undefined;
      while (element && element !== document.body) {
        if (element === floatingRef.current) return;
        if (element === referenceRef.current) return;
        element = element?.parentElement;
      }
      toolboxMenu.close(id);
    };
    window.addEventListener("mousedown", onMousedown);
    return () => window.removeEventListener("mousedown", onMousedown);
  }, [isOpen, id, floatingRef, referenceRef]);
  return (
    <>
      <Button
        ref={refs.setReference}
        title={title}
        Icon={Icon}
        onClick={() => toolboxMenu.toggle(id)}
        onAltClick={() => toolboxMenu.toggle(id)}
        intent={ButtonIntent.NONE}
        state={ButtonState.NONE}
      />
      <FloatingPortal>
        <Cluster visible={isOpen}>
          <div
            className="bg-elevation-0 text-sm p-2 shadow-md border-gray-300 border rounded z-50"
            ref={refs.setFloating}
            style={{
              position: strategy,
              top: y ?? 0,
              left: x ?? 0,
            }}
          >
            {children}
            <svg
              className="w-4 flex-shrink-0 absolute overflow-visible text-white"
              ref={arrowRef}
              viewBox="0 0 20 20"
              style={{ left: -15, top: arrowY }}
            >
              <path
                d="M 19,0 10,10 19,20"
                // d="M 0,0 10,10 20,0"
                fill="currentColor"
                stroke="black"
                className="stroke-gray-300"
              />
            </svg>
          </div>
        </Cluster>
      </FloatingPortal>
    </>
  );
});
